import i18n from '@/utils/i18n';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Dropdown, DropdownPosition } from '@usepepper/ui/atoms/dropdown';
import { Icons, Popover, Typography } from '@usepepper/sprout-kit-ui';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { KeyboardShortcutBadge } from '@/experiences/sales-portal/components/keyboard-shortcut-badge';
import { Pressable, View } from 'react-native';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';

const ICON_SIZE = 16;

export interface QuickActionFlyoutRenderProps {
  closeMenu: () => void;
}

interface QuickActionBase {
  detail?: string;
  icon?: ReactNode;
  key: string;
  label: string;
}

export interface SelectQuickAction extends QuickActionBase {
  onSelect: () => void;
  renderFlyout?: never;
}

export interface FlyoutQuickAction extends QuickActionBase {
  onSelect?: never;
  renderFlyout: (props: QuickActionFlyoutRenderProps) => ReactNode;
}

export type QuickAction = SelectQuickAction | FlyoutQuickAction;

interface QuickActionMenuProps {
  actions: QuickAction[];
  isOpen: boolean;
  keyboardScope: SalesPortalKeyboardScope;
  onActionSelect: (action: QuickAction) => void;
  onClose: () => void;
  position?: DropdownPosition;
  trigger: ReactNode;
}

const MENU_WIDTH = 240;
const FLYOUT_CLOSE_DELAY_MS = 140;
const FLYOUT_MIN_WIDTH = 288;

interface QuickActionRowProps {
  detail?: string;
  focusOnHighlight: (node: View | null) => void;
  icon?: ReactNode;
  isHighlighted: boolean;
  isOpen?: boolean;
  label: string;
  onFocus: () => void;
  onPress: () => void;
  trailing?: ReactNode;
}

const QuickActionRow: React.FC<QuickActionRowProps> = ({
  detail,
  focusOnHighlight,
  icon,
  isHighlighted,
  isOpen,
  label,
  onFocus,
  onPress,
  trailing,
}) => {
  const [styles] = useStyles();

  return (
    <Pressable
      accessibilityRole="menuitem"
      accessibilityState={{
        selected: isHighlighted,
        ...(isOpen !== undefined ? { expanded: isOpen } : {}),
      }}
      onFocus={onFocus}
      onPress={(e) => {
        e.preventDefault();
        onPress();
      }}
      ref={isHighlighted ? focusOnHighlight : undefined}
      style={[
        styles.actionRow,
        isHighlighted || isOpen ? styles.actionRowHighlighted : null,
      ]}
    >
      <View style={styles.actionLeft}>
        {icon ? <View style={styles.iconSlot}>{icon}</View> : null}
        <Typography
          color="$textPrimary"
          flexShrink={1}
          numberOfLines={1}
          size="$sm"
        >
          {label}
        </Typography>
      </View>
      {detail ? (
        <Typography color="$textSecondary" size="$sm">
          {detail}
        </Typography>
      ) : null}
      {trailing}
    </Pressable>
  );
};

export const QuickActionMenu: React.FC<QuickActionMenuProps> = ({
  actions,
  isOpen,
  keyboardScope,
  onActionSelect,
  onClose,
  position = 'bottom-start',
  trigger,
}) => {
  const [styles, theme] = useStyles();

  // Callback ref that focuses the highlighted Pressable when it mounts/updates.
  // Manual highlight state is used instead of native :focus styles because
  // React Native Web's Pressable does not expose reliable cross-platform focus
  // styling — onFocus/onBlur would require extra state management with no benefit.
  const focusHighlighted = useCallback((node: View | null) => {
    node?.focus();
  }, []);

  // Tracks arrow-key navigation offset. Resets when menu closes.
  // Default highlight is always index 0 when open — null means "no arrow navigation yet".
  const [highlightOffset, setHighlightOffset] = useState<number | null>(null);
  const highlightedIndex = isOpen ? (highlightOffset ?? 0) : null;

  const [openFlyoutKey, setOpenFlyoutKey] = useState<string | null>(null);
  const isFlyoutOpen = openFlyoutKey !== null;

  // Reset the highlight and flyout when the menu closes — including close
  // paths that never pass through this component's callbacks.
  useEffect(() => {
    if (isOpen) {
      return;
    }
    setHighlightOffset(null);
    setOpenFlyoutKey(null);
  }, [isOpen]);

  const handleAction = useCallback(
    (action: QuickAction) => {
      if (action.renderFlyout) {
        setOpenFlyoutKey(action.key);
        return;
      }
      onActionSelect(action);
    },
    [onActionSelect],
  );

  const selectHighlighted = useCallback(() => {
    const index = highlightedIndex ?? 0;
    if (actions[index]) {
      handleAction(actions[index]);
    }
  }, [actions, handleAction, highlightedIndex]);

  const navigateHighlight = useCallback(
    (direction: 'up' | 'down') => {
      const len = actions.length;
      if (len === 0) {
        return;
      }
      setHighlightOffset((prev) => {
        const current = prev ?? 0;
        return direction === 'down'
          ? (current + 1) % len
          : (current - 1 + len) % len;
      });
    },
    [actions.length],
  );

  // Q-to-close and Escape don't need enableOnFormTags — the parent handles
  // Q-to-open via onKeyPress, and these only fire when focus leaves the input.
  // Both stay enabled while a flyout is open and close everything.
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.QuickActions,
    () => onClose(),
    [onClose],
    {},
    isOpen,
    keyboardScope,
  );

  useScreenScopedKeyboardShortcuts(
    Key.Escape,
    () => onClose(),
    [onClose],
    {},
    isOpen,
    keyboardScope,
  );

  // Arrow and Enter shortcuts use the dedicated QUICK_ACTION_MENU scope so
  // they don't propagate to the table's row navigation. They are disabled
  // while a flyout is open so the flyout can control the keys.
  const isMenuNavigationEnabled = isOpen && !isFlyoutOpen;

  useScreenScopedKeyboardShortcuts(
    Key.ArrowDown,
    () => navigateHighlight('down'),
    [navigateHighlight],
    {},
    isMenuNavigationEnabled,
    SalesPortalKeyboardScope.QUICK_ACTION_MENU,
    true,
  );

  useScreenScopedKeyboardShortcuts(
    Key.ArrowUp,
    () => navigateHighlight('up'),
    [navigateHighlight],
    {},
    isMenuNavigationEnabled,
    SalesPortalKeyboardScope.QUICK_ACTION_MENU,
    true,
  );

  useScreenScopedKeyboardShortcuts(
    Key.Enter,
    () => selectHighlighted(),
    [selectHighlighted],
    {},
    isMenuNavigationEnabled && highlightedIndex !== null,
    SalesPortalKeyboardScope.QUICK_ACTION_MENU,
    true,
  );

  return (
    <Dropdown
      containerStyle={styles.menuContainer}
      isVisible={isOpen}
      onClose={onClose}
      position={position}
      trigger={trigger}
    >
      <View>
        <View style={styles.sectionHeader}>
          <Typography color="$textSecondary" size="$xs">
            {i18n.t('sales_portal.order_entry.quick_actions.title')}
          </Typography>
          <KeyboardShortcutBadge shortcut={SalesPortalShortcuts.QuickActions} />
        </View>
        {actions.map((action, index) =>
          action.renderFlyout ? (
            <Popover
              allowFlip
              disableFocus
              hoverable={{ delay: { close: FLYOUT_CLOSE_DELAY_MS } }}
              key={action.key}
              onOpenChange={(open) => {
                setOpenFlyoutKey((prev) => {
                  if (open) {
                    return action.key;
                  }
                  return prev === action.key ? null : prev;
                });
              }}
              open={action.key === openFlyoutKey}
              placement="right-start"
            >
              <Popover.Anchor>
                <QuickActionRow
                  detail={action.detail}
                  focusOnHighlight={focusHighlighted}
                  icon={action.icon}
                  isHighlighted={highlightedIndex === index}
                  isOpen={action.key === openFlyoutKey}
                  label={action.label}
                  onFocus={() => setHighlightOffset(index)}
                  onPress={() => handleAction(action)}
                  trailing={
                    <View style={styles.chevronSlot}>
                      <Icons.ChevronRight
                        color={theme.sk.color.text.secondary}
                        size={ICON_SIZE}
                      />
                    </View>
                  }
                />
              </Popover.Anchor>
              <Popover.Content minWidth={FLYOUT_MIN_WIDTH} padding="$16">
                <View>{action.renderFlyout({ closeMenu: onClose })}</View>
              </Popover.Content>
            </Popover>
          ) : (
            <QuickActionRow
              detail={action.detail}
              focusOnHighlight={focusHighlighted}
              icon={action.icon}
              isHighlighted={highlightedIndex === index}
              key={action.key}
              label={action.label}
              onFocus={() => setHighlightOffset(index)}
              onPress={() => handleAction(action)}
            />
          ),
        )}
      </View>
    </Dropdown>
  );
};

const useStyles = createStyles((theme) => ({
  actionLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 1,
    gap: theme.sk.spacing.spaceSmall,
    minWidth: 0,
  },
  actionRow: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusSmall,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  actionRowHighlighted: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  chevronSlot: {
    flexShrink: 0,
  },
  iconSlot: {
    alignItems: 'center',
    minWidth: ICON_SIZE,
  },
  menuContainer: {
    padding: theme.sk.spacing.spaceSmall,
    width: MENU_WIDTH,
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    marginBottom: theme.sk.spacing.spaceXsmall,
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
}));
